// Sidebar badge state — one module, two counts (chat-identity claims +
// pending PL rows). The composite "Inbox" badge in the sidebar reads
// from this same store.
//
// Update model: 60s background poll + immediate refresh on action.
// Pages that approve a claim, link/dismiss a pending row, mark a parcel
// ready-for-pickup, etc. should call `invalidate('claims')` or
// `invalidate('imports')` right after their axios call resolves. The
// composable refetches just that count (or both) without waiting for
// the next poll tick.
//
// An axios response interceptor (set up at app boot) also auto-fires
// `invalidate()` on a small allowlist of mutating endpoints so most
// pages don't need to import this at all.

import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

const POLL_MS = 60_000

const ENDPOINTS = {
  claims: '/shipment/customers/chat-identity-claims/pending-count',
  imports: '/shipment/pending-pl-rows/badge',
  unclaimed: '/shipment/incoming-parcels/unclaimed-count',
}

// Module-level state so every component sees the same numbers.
const claimsCount    = ref(0)
const importsCount   = ref(0)
const unclaimedCount = ref(0)
const loaded         = ref(false)

const inFlight = { claims: false, imports: false, unclaimed: false }
let intervalId = null

async function fetchOne(key) {
  if (inFlight[key]) return
  inFlight[key] = true
  try {
    const { data } = await axios.get(ENDPOINTS[key])
    if (key === 'claims') {
      claimsCount.value = (data && typeof data.count === 'number') ? data.count : 0
    } else if (key === 'imports') {
      // Endpoint returns { pending: <int> }
      importsCount.value = (data && typeof data.pending === 'number') ? data.pending : 0
    } else if (key === 'unclaimed') {
      unclaimedCount.value = (data && typeof data.count === 'number') ? data.count : 0
    }
    loaded.value = true
  } catch {
    // Silent — endpoints are auth-gated; 401 on the login page is expected.
  } finally {
    inFlight[key] = false
  }
}

async function fetchAll() {
  await Promise.all([fetchOne('claims'), fetchOne('imports'), fetchOne('unclaimed')])
}

/**
 * Force a refresh of one badge (or all). Pages call this after a
 * mutation that affects the count. Cheap; debounced by the in-flight
 * guard, so consecutive calls within the same tick collapse to one.
 *
 * @param {'claims'|'imports'|'unclaimed'|'all'} [key='all']
 */
export function invalidateSidebarBadge(key = 'all') {
  if (key === 'all') return fetchAll()
  if (ENDPOINTS[key]) return fetchOne(key)
}

/**
 * Read the badges from any component.
 */
export function useSidebarBadges() {
  const inboxTotal = computed(
    () => claimsCount.value + importsCount.value + unclaimedCount.value,
  )

  return {
    claimsCount,
    importsCount,
    unclaimedCount,
    inboxTotal,
    loaded,
    refresh: fetchAll,
    invalidate: invalidateSidebarBadge,
  }
}

/**
 * Start the polling loop + install the axios interceptor. Mount this
 * once from the top-level authenticated layout.
 */
export function useSidebarBadgesPoller() {
  onMounted(() => {
    if (intervalId) return
    fetchAll()
    intervalId = setInterval(fetchAll, POLL_MS)
    installAxiosAutoInvalidate()
  })
}

// ── Axios auto-invalidation ─────────────────────────────────────────────
//
// Most mutating endpoints don't need code changes — we recognize a small
// allowlist and refresh the affected badge as soon as the API call
// returns. Pages can still call invalidateSidebarBadge() explicitly for
// finer control.
let interceptorInstalled = false

function installAxiosAutoInvalidate() {
  if (interceptorInstalled) return
  interceptorInstalled = true

  axios.interceptors.response.use(resp => {
    try {
      const url = (resp.config?.url || '').toLowerCase()
      const method = (resp.config?.method || 'get').toLowerCase()
      if (method === 'get') return resp

      // Chat-identity claim mutations
      if (url.includes('chat-identity-claims')) {
        fetchOne('claims')
      }

      // Pending PL row mutations: resolve / link / dismiss / delete.
      // Resolving a row also creates the parcel — if it goes in unattached
      // it bumps the unclaimed counter, so refresh both.
      if (url.includes('pending-pl-rows')) {
        fetchOne('imports')
        fetchOne('unclaimed')
      }

      // Customer creation/verification can promote unidentified → pending
      // which makes them visible to operators; surface it via the claims
      // badge poll (markVerified writes to contacts + claims tables).
      if (url.includes('/customers') && (method === 'post' || method === 'put' || method === 'patch')) {
        fetchOne('claims')
      }

      // Claim-parcel writes attach an unclaimed parcel to a customer →
      // the unclaimed count drops. Covers both the web /shipment/claims/*
      // endpoints and the mobile equivalents under /mobile/shipment/claims.
      if (url.includes('shipment/claims') || url.includes('claims/attach') || url.includes('claims/reassign')) {
        fetchOne('unclaimed')
      }

      // Bulk parcel updates that may set customer_id also affect the count.
      if (url.includes('incoming-parcels') && url.includes('bulk')) {
        fetchOne('unclaimed')
      }
    } catch {
      // never let an interceptor failure poison the original response
    }

    return resp
  })
}
